import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import PageTransition from '../../components/feature/PageTransition';

const QuotePage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productType: '',
    capacity: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    document.title = '삼원테크 | 견적 문의 - 스테인리스 물탱크 제작·설치 상담';
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      '삼원테크 견적 문의. STS패널형·원통형·라이닝 물탱크 제작·설치 상담. 전화 061-373-3221, 이메일 samwontk@hanmail.net. 빠른 답변 보장.'
    );
    const schema = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/quote#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/quote`,
      "name": "삼원테크 견적 문의",
      "description": "STS패널형·원통형·라이닝 물탱크 제작·설치 견적 문의 페이지.",
      "inLanguage": "ko-KR",
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "홈", "item": import.meta.env.VITE_SITE_URL || 'https://example.com' },
          { "@type": "ListItem", "position": 2, "name": "견적 문의", "item": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/quote` }
        ]
      }
    };
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-quote';
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => { document.getElementById('schema-quote')?.remove(); };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.message.length > 500) return;
    setIsSubmitting(true);

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataObj = new FormData(formElement);
      const urlEncodedData = new URLSearchParams();
      formDataObj.forEach((value, key) => {
        urlEncodedData.append(key, value.toString());
      });

      await fetch('https://readdy.ai/api/form/d6n5qtdv117fnkj2hdbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: urlEncodedData.toString(),
      });

      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        productType: '',
        capacity: '',
        message: '',
      });

      setTimeout(() => {
        setSubmitStatus('idle');
      }, 6000);
    } catch {
      setIsSubmitting(false);
      setSubmitStatus('error');
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactCards = [
    {
      icon: 'ri-phone-line',
      title: '전화 문의',
      info: '061-373-3221',
      description: '평일 08:00 ~ 18:00',
      href: 'tel:061-373-3221',
    },
    {
      icon: 'ri-mail-send-line',
      title: '이메일 문의',
      info: 'samwontk@hanmail.net',
      description: '24시간 접수 가능',
      href: 'mailto:samwontk@hanmail.net',
    },
    {
      icon: 'ri-map-pin-line',
      title: '회사 위치',
      info: '전남 화순군 동면 동농공길 26-3',
      description: '방문 전 사전 연락 바랍니다',
      href: '#',
    },
  ];

  return (
    <PageTransition>
      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Hero Section */}
        <div className="relative h-[55vh] w-full overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/2d576a2c5bd3ef0148d6945391d8aaab.png"
              alt="견적 문의"
              className="w-full h-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
          </div>

          <div className="relative h-full flex flex-col items-center justify-center text-white px-4 w-full">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center w-full"
            >
              <span className="inline-flex items-center gap-2 text-teal-300 text-sm font-semibold tracking-widest uppercase mb-4">
                <span className="w-8 h-px bg-teal-400"></span>
                QUOTE REQUEST
                <span className="w-8 h-px bg-teal-400"></span>
              </span>
              <h1 className="text-5xl md:text-6xl font-bold mt-2 mb-5">
                견적 문의
              </h1>
              <p className="text-lg md:text-xl max-w-2xl mx-auto leading-relaxed text-white/90">
                물탱크 제작·설치에 관한 궁금한 사항을 남겨주시면<br />
                빠르게 답변 드리겠습니다.
              </p>
            </motion.div>
          </div>
        </div>

        {/* Contact Cards */}
        <div className="max-w-[1200px] mx-auto px-4 -mt-14 relative z-10">
          <div className="grid md:grid-cols-3 gap-5">
            {contactCards.map((card, index) => (
              <motion.a
                key={card.title}
                href={card.href}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-7 shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer block"
              >
                <div className="w-14 h-14 bg-teal-50 rounded-xl flex items-center justify-center mb-5 border border-teal-100">
                  <i className={`${card.icon} text-2xl text-teal-600`} />
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-1">{card.title}</h3>
                <p className="text-sm font-semibold text-teal-600 mb-1">{card.info}</p>
                <p className="text-xs text-gray-500">{card.description}</p>
              </motion.a>
            ))}
          </div>
        </div>

        {/* Main Form Section */}
        <div className="max-w-[1200px] mx-auto px-4 py-20">
          <div className="grid lg:grid-cols-5 gap-14 items-start">

            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-3"
            >
              <span className="inline-flex items-center gap-2 text-teal-600 text-xs font-semibold tracking-widest uppercase mb-3">
                <span className="w-6 h-px bg-teal-500"></span>
                문의 양식
              </span>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">
                견적 요청서 작성
              </h2>
              <p className="text-gray-500 text-sm mb-8 leading-relaxed">
                아래 양식을 작성해 주시면 담당자가 확인 후 연락드립니다.
              </p>

              <form
                id="quote-form"
                data-readdy-form
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                {/* 이름 + 연락처 */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                      성함 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="홍길동"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:border-teal-500 focus:outline-none focus:bg-white transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      연락처 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="010-0000-0000"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:border-teal-500 focus:outline-none focus:bg-white transition-all duration-300"
                    />
                  </div>
                </div>

                {/* 이메일 + 회사명 */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      이메일 <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="example@email.com"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:border-teal-500 focus:outline-none focus:bg-white transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                      회사명 / 기관명
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="(주)삼원테크"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:border-teal-500 focus:outline-none focus:bg-white transition-all duration-300"
                    />
                  </div>
                </div>

                {/* 제품 종류 + 용량 */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="productType" className="block text-sm font-semibold text-gray-700 mb-2">
                      제품 종류 <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <select
                        id="productType"
                        name="productType"
                        value={formData.productType}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:border-teal-500 focus:outline-none appearance-none cursor-pointer transition-all duration-300"
                      >
                        <option value="">제품을 선택해 주세요</option>
                        <option value="STS패널형 물탱크">STS패널형 물탱크</option>
                        <option value="STS원통형 물탱크">STS원통형 물탱크</option>
                        <option value="라이닝 물탱크">라이닝 물탱크</option>
                        <option value="기타">기타</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="capacity" className="block text-sm font-semibold text-gray-700 mb-2">
                      희망 용량 (톤)
                    </label>
                    <input
                      type="text"
                      id="capacity"
                      name="capacity"
                      value={formData.capacity}
                      onChange={handleChange}
                      placeholder="예: 10톤, 50톤"
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:border-teal-500 focus:outline-none focus:bg-white transition-all duration-300"
                    />
                  </div>
                </div>

                {/* 문의 내용 */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    문의 내용 <span className="text-red-400">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    maxLength={500}
                    placeholder="설치 장소, 규격, 납기 등 문의하실 내용을 자유롭게 작성해 주세요."
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 text-sm focus:border-teal-500 focus:outline-none focus:bg-white resize-none transition-all duration-300"
                  />
                  <div className="flex justify-between mt-1.5">
                    <span className={`text-xs ${formData.message.length > 500 ? 'text-red-500' : 'text-gray-400'}`}>
                      {formData.message.length > 500 ? '500자를 초과할 수 없습니다.' : ''}
                    </span>
                    <span className="text-xs text-gray-400">{formData.message.length}/500</span>
                  </div>
                </div>

                {/* 제출 버튼 */}
                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500}
                  className="w-full bg-teal-600 text-white py-4 rounded-xl font-semibold text-base hover:bg-teal-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap shadow-lg hover:shadow-xl cursor-pointer"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xl" />
                      전송 중...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line text-xl" />
                      견적 문의 보내기
                    </>
                  )}
                </button>

                {/* 성공 메시지 */}
                {submitStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-teal-50 border border-teal-200 text-teal-700 px-5 py-4 rounded-xl flex items-center gap-4"
                  >
                    <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-check-line text-xl text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-sm">문의가 성공적으로 접수되었습니다!</p>
                      <p className="text-xs opacity-80 mt-0.5">담당자가 확인 후 빠르게 연락드리겠습니다.</p>
                    </div>
                  </motion.div>
                )}

                {/* 오류 메시지 */}
                {submitStatus === 'error' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-red-50 border border-red-200 text-red-600 px-5 py-4 rounded-xl flex items-center gap-4"
                  >
                    <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                      <i className="ri-close-line text-xl text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-sm">전송에 실패했습니다.</p>
                      <p className="text-xs opacity-80 mt-0.5">잠시 후 다시 시도하거나 전화로 문의해 주세요.</p>
                    </div>
                  </motion.div>
                )}
              </form>
            </motion.div>

            {/* Right Side */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:col-span-2 space-y-6"
            >
              {/* 이미지 */}
              <div className="relative h-64 rounded-2xl overflow-hidden shadow-lg">
                <img
                  src="https://static.readdy.ai/image/75975fdd6a8419c14194c00fe1023b5f/a622e2ab3f4ddef9675dcd029ea73e5d.jpeg"
                  alt="STS원통형 물탱크"
                  className="w-full h-full object-cover object-top"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-5 left-5 text-white">
                  <p className="text-lg font-bold">30년 전통의 기술력</p>
                  <p className="text-white/80 text-sm">믿을 수 있는 물탱크 전문 제조업체</p>
                </div>
              </div>

              {/* 실적 */}
              <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-7 border border-teal-100">
                <h3 className="text-base font-bold text-gray-900 mb-5">삼원테크 주요 실적</h3>
                <div className="grid grid-cols-2 gap-5">
                  {[
                    { value: '30+', label: '업력 (년)' },
                    { value: '1,000+', label: '시공 현장' },
                    { value: '100%', label: '품질 보증' },
                    { value: '24h', label: '빠른 답변' },
                  ].map((stat) => (
                    <div key={stat.label} className="text-center">
                      <div className="text-2xl font-bold text-teal-600 mb-1">{stat.value}</div>
                      <p className="text-gray-500 text-xs">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* 제품 안내 */}
              <div className="bg-white border border-gray-100 rounded-2xl p-7 shadow-sm">
                <h3 className="text-base font-bold text-gray-900 mb-4">제품 안내</h3>
                <ul className="space-y-3">
                  {[
                    { icon: 'ri-water-flash-line', name: 'STS패널형 물탱크', desc: '용접식 스테인리스 패널 구조' },
                    { icon: 'ri-drop-line', name: 'STS원통형 물탱크', desc: '원통형 고강도 스테인리스 탱크' },
                    { icon: 'ri-shield-check-line', name: '라이닝 물탱크', desc: '기존 탱크 내부 라이닝 보수' },
                  ].map((product) => (
                    <li key={product.name} className="flex items-center gap-3">
                      <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg border border-teal-100 flex-shrink-0">
                        <i className={`${product.icon} text-teal-600 text-base`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{product.name}</p>
                        <p className="text-xs text-gray-500">{product.desc}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              {/* 전화 CTA */}
              <a
                href="tel:061-373-3221"
                className="flex items-center gap-4 bg-teal-600 text-white rounded-2xl p-6 hover:bg-teal-700 transition-all duration-300 cursor-pointer shadow-lg hover:shadow-xl"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                  <i className="ri-phone-line text-2xl" />
                </div>
                <div>
                  <p className="text-xs font-semibold opacity-80 mb-0.5">전화 상담</p>
                  <p className="text-xl font-bold tracking-wide">061-373-3221</p>
                  <p className="text-xs opacity-70 mt-0.5">평일 08:00 ~ 18:00</p>
                </div>
              </a>
            </motion.div>
          </div>
        </div>

        <Footer />
      </div>
    </PageTransition>
  );
};

export default QuotePage;
